import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import LimitedPageContainer from '../../components/limited-page-block/limited-page-container.component';
import ContentHeader from '../../components/content-header/content-header.component';
import Loader from '../../components/loader/loader.component';
import FilteredWidget from '../../components/filtered-widget/filtered-widget.component';
import WidgetRow from '../../components/widget-row/widget-row.component';
import CheckAccess from '../../components/check-access/check-access.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import StatisticsBlock from '../../components/statistics-block/statistics-block.component';
import Controls from '../app/controls/controls.component';
import PortfolioFilter from '../app/controls/portfolio-filter/portfolio-filter.component';
import DashboardWidgetAccountSummary
  from './dashboard-widget-account-summary/dashboard-widget-account-summary.component';
import DashboardWidgetDealLocations
  from './dashboard-widget-deal-locations/dashboard-widget-deal-locations.component';
import DashboardWidgetDealSourcesByBroker
  from './dashboard-widget-deal-sources-by-broker/dashboard-widget-deal-sources-by-broker.component';
import DashboardWidgetDealsInDueDiligence
  from './dashboard-widget-deals-in-due-diligence/dashboard-widget-deals-in-due-diligence.component';
import DashboardWidgetRecentDealUpdates
  from './dashboard-widget-recent-deal-updates/dashboard-widget-recent-deal-updates.component';
import DashboardWidgetUserDealTasks
  from './dashboard-widget-user-deal-tasks/dashboard-widget-user-deal-tasks.component';
import DashboardWidgetUserMentions
  from './dashboard-widget-user-mentions/dashboard-widget-user-mentions.component';
import DashboardWidgetNewDealsByPeriod
  from './dashboard-widget-new-deals-by-period/dashboard-widget-new-deals-by-period.component';
import DashboardWidgetDeadDealReasons
  from './dashboard-widget-dead-deal-reasons/dashboard-widget-dead-deal-reasons.component';
import DashboardWidgetDealsByMsa from './dashboard-widget-deals-by-msa/dashboard-widget-deals-by-msa.component';
import DashboardWidgetDealsByType from './dashboard-widget-deals-by-type/dashboard-widget-deals-by-type.component';
import DashboardWidgetDealPipelineFunnel
  from './dashboard-widget-deal-pipeline-funnel/dashboard-widget-deal-pipeline-funnel.component';
import StructureService from '../../services/structure-service';
import FormatService from '../../services/format-service';
import useScreenSmall from '../../hooks/use-screen-small';
import useCurrentUserPermissions from '../../hooks/use-current-user-permissions';
import getDashboardData from './dashboard.action';
import { toggleCustomizeMode, saveWidgetSettings } from '../app/controls/controls.action';
import { deleteActivityComment, saveActivityComment } from '../activity/activity.action';
import {
  setPortfolioGroupIdsFilter, setPortfolioIdsFilter,
} from '../app/controls/portfolio-filter/portfolio-filter.action';
import { getListContactCompanyData, getListContactPersonData } from '../app/search-list/search-list.action';
import {
  ROUTE_ACTIVITY_LIST, ROUTE_CONTACT_LIST, ROUTE_DASHBOARD, ROUTE_DEAL_LIST,
} from '../../constants/system/routes';
import {
  QUERY_PARAM_CREATE, QUERY_PARAM_PORTFOLIO_GROUP_IDS, QUERY_PARAM_PORTFOLIO_IDS, QUERY_PARAM_CATEGORY,
} from '../../constants/system/query-params';
import {
  SETTING_NAME_ACCOUNT_WIDGET_SETTINGS, SETTING_NAME_WIDGET_SETTINGS,
} from '../../constants/entity/setting-names';
import { ACTIVITY_CATEGORY_CAPITAL_RAISE } from '../../constants/entity/activity-categories';
import {
  DASHBOARD_VIEW, DASHBOARD_STATISTICS_VIEW, DASHBOARD_PORTFOLIO_FILTER_VIEW, DASHBOARD_ASSET_LOCATIONS_VIEW,
  DASHBOARD_DEAL_SOURCES_VIEW, DASHBOARD_DEALS_IN_DUE_DILIGENCE_VIEW, DASHBOARD_RECENT_DEAL_UPDATES_VIEW,
  DASHBOARD_USER_DEAL_TASKS_VIEW, DASHBOARD_NEW_DEALS_BY_PERIOD_VIEW, DASHBOARD_DEAD_DEAL_REASONS_VIEW,
  DASHBOARD_DEALS_BY_MSA_VIEW, DASHBOARD_DEALS_BY_TYPE_VIEW, DEAL_CREATE, ACTIVITY_CREATE, CONTACT_CREATE,
  DASHBOARD_DEAL_PIPELINE_FUNNEL_VIEW, DASHBOARD_ACCOUNT_SUMMARY_VIEW, DASHBOARD_USER_MENTIONS_VIEW,
} from '../../constants/entity/permissions';
import {
  WIDGET_NAME_DEAL_LOCATIONS, WIDGET_NAME_DEAL_SOURCES_BY_BROKER, WIDGET_NAME_DEALS_IN_DUE_DILIGENCE,
  WIDGET_NAME_RECENT_DEAL_UPDATES, WIDGET_NAME_USER_DEAL_TASKS, WIDGET_NAME_NEW_DEALS_BY_PERIOD,
  WIDGET_NAME_DEAD_DEAL_REASONS, WIDGET_NAME_DEALS_BY_MSA, WIDGET_NAME_DEALS_BY_TYPE,
  WIDGET_NAME_DEAL_PIPELINE_FUNNEL, WIDGET_NAME_ACCOUNT_SUMMARY, WIDGET_NAME_USER_MENTIONS,
} from '../../constants/system/widget-names';
import { SIGN_DOT } from '../../constants/system/signs';

const Dashboard: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    statisticsData,
    portfolioGroupList,
    assetList,
    accountSummaryData,
    dealsInDueDiligenceData,
    dealSourcesByBrokerData,
    recentlyUpdatedDealsData,
    userDealTasksData,
    userMentionsData,
    newDealsByPeriodData,
    deadDealReasonsData,
    dealsByMsaData,
    dealsByTypeData,
    dealPipelineFunnelData,
    isDataLoaded,
    isActivityCommentSaving,
    newCommentList,
    currentUser,
    contactCompanyList,
    contactPersonList,
    contactLinkedUserList,
    tagList,
    portfolioIdsFilter,
    portfolioGroupIdsFilter,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const permissionList: string[] = useCurrentUserPermissions();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryFilterPortfolioIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS].toString().split(SIGN_DOT)
        .map((portfolioId: string): number => FormatService.parseInt(portfolioId)) : null
  );
  const queryFilterPortfolioGroupIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS].toString().split(SIGN_DOT)
        .map((portfolioGroupId: string): number => FormatService.parseInt(portfolioGroupId)) : null
  );

  const accountData: Account | null = (currentUser && currentUser.account) || null;
  const accountSettings: Setting[] = (accountData && accountData.settings) || [];
  const accountWidgetSettingsSetting: Setting | null = accountSettings && accountSettings.length
    ? (accountSettings.find((settingItem: Setting): boolean => (
      settingItem.name === SETTING_NAME_ACCOUNT_WIDGET_SETTINGS
    )) || null) : null;
  const accountWidgetSettings: Record<string, WidgetItem> | null = accountWidgetSettingsSetting
    ? accountWidgetSettingsSetting.value : null;

  const [dashboardWidgetSettings, setDashboardWidgetSettings]: [Record<string, WidgetItem> | null, Function]
    = useState<StateValueDefault>(null);

  const pageTitle: string = 'Dashboard';
  const createButtonTitle: string = 'New Deal';
  const createGroupButtonTitle: string = 'New Activity';
  const createAdditionalItemButtonTitle: string = 'New Contact';

  const dashboardViewPermissions: string[] = useMemo((): string[] => [DASHBOARD_VIEW], []);
  const dashboardStatisticsViewPermissions: string[] = useMemo((): string[] => [DASHBOARD_STATISTICS_VIEW], []);
  const dashboardPortfolioFilterViewPermissions: string[] = useMemo((): string[] => [
    DASHBOARD_PORTFOLIO_FILTER_VIEW,
  ], []);
  const dashboardAccountSummaryPermissions: string[] = useMemo((): string[] => [DASHBOARD_ACCOUNT_SUMMARY_VIEW], []);
  const dashboardAssetLocationsViewPermissions: string[] = useMemo((): string[] => [
    DASHBOARD_ASSET_LOCATIONS_VIEW,
  ], []);
  const dashboardDealSourcesViewPermissions: string[] = useMemo((): string[] => [DASHBOARD_DEAL_SOURCES_VIEW], []);
  const dashboardDealsInDueDiligenceViewPermissions: string[] = useMemo((): string[] => [
    DASHBOARD_DEALS_IN_DUE_DILIGENCE_VIEW,
  ], []);
  const dashboardRecentDealUpdatesViewPermissions: string[] = useMemo((): string[] => [
    DASHBOARD_RECENT_DEAL_UPDATES_VIEW,
  ], []);
  const dashboardUserDealTasksPermissions: string[] = useMemo((): string[] => [DASHBOARD_USER_DEAL_TASKS_VIEW], []);
  const dashboardUserMentionsPermissions: string[] = useMemo((): string[] => [DASHBOARD_USER_MENTIONS_VIEW], []);
  const dashboardNewDealsByPeriodPermissions: string[] = useMemo((): string[] => [
    DASHBOARD_NEW_DEALS_BY_PERIOD_VIEW,
  ], []);
  const dashboardDeadDealReasonsPermissions: string[] = useMemo((): string[] => [DASHBOARD_DEAD_DEAL_REASONS_VIEW], []);
  const dashboardDealsByMsaPermissions: string[] = useMemo((): string[] => [DASHBOARD_DEALS_BY_MSA_VIEW], []);
  const dashboardDealsByTypePermissions: string[] = useMemo((): string[] => [DASHBOARD_DEALS_BY_TYPE_VIEW], []);
  const dashboardDealPipelineFunnelPermissions: string[] = useMemo((): string[] => [
    DASHBOARD_DEAL_PIPELINE_FUNNEL_VIEW,
  ], []);

  const WIDGET_SETTING_PERMISSION_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [WIDGET_NAME_ACCOUNT_SUMMARY]: DASHBOARD_ACCOUNT_SUMMARY_VIEW,
    [WIDGET_NAME_DEAL_LOCATIONS]: DASHBOARD_ASSET_LOCATIONS_VIEW,
    [WIDGET_NAME_DEAL_SOURCES_BY_BROKER]: DASHBOARD_DEAL_SOURCES_VIEW,
    [WIDGET_NAME_DEALS_IN_DUE_DILIGENCE]: DASHBOARD_DEALS_IN_DUE_DILIGENCE_VIEW,
    [WIDGET_NAME_RECENT_DEAL_UPDATES]: DASHBOARD_RECENT_DEAL_UPDATES_VIEW,
    [WIDGET_NAME_USER_DEAL_TASKS]: DASHBOARD_USER_DEAL_TASKS_VIEW,
    [WIDGET_NAME_DEALS_BY_MSA]: DASHBOARD_DEALS_BY_MSA_VIEW,
    [WIDGET_NAME_DEALS_BY_TYPE]: DASHBOARD_DEALS_BY_TYPE_VIEW,
    [WIDGET_NAME_NEW_DEALS_BY_PERIOD]: DASHBOARD_NEW_DEALS_BY_PERIOD_VIEW,
    [WIDGET_NAME_DEAD_DEAL_REASONS]: DASHBOARD_DEAD_DEAL_REASONS_VIEW,
    [WIDGET_NAME_DEAL_PIPELINE_FUNNEL]: DASHBOARD_DEAL_PIPELINE_FUNNEL_VIEW,
  }), []);

  const portfolioList: Portfolio[] = useMemo((): Portfolio[] => (
    portfolioGroupList && portfolioGroupList.length ? portfolioGroupList.reduce((
      resultPortfolioList: Portfolio[], portfolioGroup: PortfolioGroup,
    ): Portfolio[] => ([
      ...resultPortfolioList,
      ...portfolioGroup.portfolios,
    ]), []) : []
  ), [portfolioGroupList]);
  const filteredPortfolioGroupList: PortfolioGroup[] = useMemo((): PortfolioGroup[] => (
    portfolioGroupList && portfolioGroupList.filter((portfolioGroupItem: PortfolioGroup): boolean => (
      !!portfolioGroupItem.portfolios.length
    ))
  ), [portfolioGroupList]);
  const extendedDashboardWidgetSettings: WidgetItem[] = useMemo((): WidgetItem[] => (
    dashboardWidgetSettings && Object.keys(dashboardWidgetSettings).length && accountWidgetSettings
      ? Object.keys(dashboardWidgetSettings).filter((widgetName: string): boolean => (
        !!widgetName && !!accountWidgetSettings[widgetName] && accountWidgetSettings[widgetName].isVisible
      )).map((widgetName: string): WidgetItem => ({
        ...dashboardWidgetSettings[widgetName],
        name: widgetName,
        permission: (widgetName && WIDGET_SETTING_PERMISSION_MAP[widgetName]) || undefined,
        title: accountWidgetSettings[widgetName].title,
      })) : []
  ), [dashboardWidgetSettings, accountWidgetSettings]);
  const withNoWidgets: boolean = extendedDashboardWidgetSettings && !extendedDashboardWidgetSettings.length;
  const isPortfolioFilterViewAllowed: boolean = StructureService.checkPermission(
    DASHBOARD_PORTFOLIO_FILTER_VIEW, permissionList,
  );

  const updateQueryParams: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_DASHBOARD,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, portfolioIdsFilter, portfolioGroupIdsFilter]);

  const onPortfolioFilterValuesChange: Function = useCallback((
    portfolioFilterGroupIds: number[], portfolioFilterIds: number[],
  ): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (portfolioFilterIds && portfolioFilterIds.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioFilterIds.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioFilterGroupIds && portfolioFilterGroupIds.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioFilterGroupIds.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_DASHBOARD,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams]);

  const getOptions: Function = useCallback((
    portfolioIdsOption?: number[], portfolioGroupIdsOption?: number[],
  ): RequestOptions => {
    let options: RequestOptions = {};

    options = portfolioIdsOption && portfolioIdsOption.length
      ? { ...options, portfolioIds: portfolioIdsOption } : options;
    options = portfolioGroupIdsOption && portfolioGroupIdsOption.length
      ? { ...options, portfolioGroupIds: portfolioGroupIdsOption } : options;

    return options;
  }, []);

  const onWidgetSettingsChange: Function = useCallback((
    widgetName: string, widgetSettings: WidgetItem, shouldBeUpdated?: boolean,
  ): void => {
    dispatch(saveWidgetSettings({
      widgetName,
      widgetSettings,
    }, (): void => {
      if (shouldBeUpdated) {
        dispatch(getDashboardData(getOptions(portfolioIdsFilter, portfolioGroupIdsFilter)));
      }
    }));
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onContactPersonSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactPersonData({
      query,
    }));
  }, []);

  const onContactPersonSearchClose: Function = useCallback((): void => {
    dispatch(getListContactPersonData({
      query: null,
    }));
  }, []);

  const onCancelForm: Function = useCallback((): void => {
    dispatch(toggleCustomizeMode(false));
  }, []);

  const onAddNewDealActivityComment: Function = useCallback((
    activityId: number, comment: string, attachmentFiles: File[], mentionContactIds: number[],
    linkedEntities: CommentEntityInput[], onSuccess?: Function,
  ): void => {
    dispatch(saveActivityComment({
      comment, attachmentFiles, activityId, mentionContactIds, linkedEntities,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const onUpdateNewDealActivityComment: Function = useCallback((
    activityId: number, comment: string, attachments: AttachmentInput[], attachmentFiles: File[], commentId: number,
    mentionContactIds: number[], onSuccess?: Function,
  ): void => {
    dispatch(saveActivityComment({
      comment, attachments, attachmentFiles, activityId, commentId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess(commentId);
      }
    }));
  }, []);

  const onDeleteNewDealActivityComment: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, activityId: number, commentId: number,
  ): void => {
    dispatch(deleteActivityComment({
      activityId,
      commentId,
    }));

    event.stopPropagation();
  }, []);

  const onCommentEntityLinkClick: Function = useCallback((link: string): void => {
    navigate(link);
  }, []);

  const onNewDealCreateButtonClick: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_CREATE]: true,
    };

    navigate({
      pathname: ROUTE_DEAL_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, []);

  const onNewActivityCreateButtonClick: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_CATEGORY]: ACTIVITY_CATEGORY_CAPITAL_RAISE,
      [QUERY_PARAM_CREATE]: true,
    };

    navigate({
      pathname: ROUTE_ACTIVITY_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, []);

  const onNewContactCreateButtonClick: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_CREATE]: true,
    };

    navigate({
      pathname: ROUTE_CONTACT_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, []);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getDashboardData(
      getOptions(
        queryFilterPortfolioIds && queryFilterPortfolioIds.length ? queryFilterPortfolioIds : portfolioIdsFilter,
        queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length
          ? queryFilterPortfolioGroupIds : portfolioGroupIdsFilter,
      ),
    ));
  }, [queryFilterPortfolioIds, queryFilterPortfolioGroupIds, portfolioIdsFilter, portfolioGroupIdsFilter]);

  useEffect((): void => {
    if (!StructureService.checkIfArraysEqual(portfolioIdsFilter, queryFilterPortfolioIds || undefined)
      || !StructureService.checkIfArraysEqual(portfolioGroupIdsFilter, queryFilterPortfolioGroupIds || undefined)) {
      updateQueryParams();
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  useEffect((): void => {
    if (!isDataLoaded) {
      if (queryFilterPortfolioIds && queryFilterPortfolioIds.length) {
        dispatch(setPortfolioIdsFilter(queryFilterPortfolioIds));
      }

      if (queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length) {
        dispatch(setPortfolioGroupIdsFilter(queryFilterPortfolioGroupIds));

        if (!queryFilterPortfolioIds || !queryFilterPortfolioIds.length) {
          dispatch(setPortfolioIdsFilter(undefined));
        }
      }

      dispatch(getDashboardData(
        getOptions(
          queryFilterPortfolioIds && queryFilterPortfolioIds.length ? queryFilterPortfolioIds : portfolioIdsFilter,
          queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length
            ? queryFilterPortfolioGroupIds : portfolioGroupIdsFilter,
        ),
      ));
    }
  }, [isDataLoaded]);

  useEffect((): void => {
    if (isDataLoaded) {
      dispatch(getDashboardData(
        getOptions(portfolioIdsFilter, portfolioGroupIdsFilter),
      ));
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  useEffect((): void => {
    if (currentUser) {
      const widgetSettingsCommon: Setting = currentUser && currentUser.settings
        && currentUser.settings.find((settingItem: Setting): boolean => (
          settingItem.name === SETTING_NAME_WIDGET_SETTINGS
        ));
      const widgetSettings: Record<string, WidgetItem> = widgetSettingsCommon && widgetSettingsCommon.value;

      if (widgetSettings && Object.keys(widgetSettings).length && accountWidgetSettings) {
        Object.keys(widgetSettings).forEach((widgetName: string): void => {
          if (accountWidgetSettings[widgetName] && accountWidgetSettings[widgetName].title) {
            widgetSettings[widgetName].title = accountWidgetSettings[widgetName].title;
          }
        });
      }

      setDashboardWidgetSettings({
        ...(widgetSettings || {}),
      });
    }
  }, [currentUser]);

  if (!isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={dashboardViewPermissions}>
      <PullToRefresh onRefresh={onRefresh}>
        <ContentHeader
          title={pageTitle}
          leftBlockComponent={isPortfolioFilterViewAllowed && !!portfolioGroupList && !isSmallScreen && (
            <PortfolioFilter
              portfolioGroupList={portfolioGroupList}
              onPortfolioFilterValuesChange={onPortfolioFilterValuesChange}
            />
          )}
        />
        <CheckAccess permissions={dashboardStatisticsViewPermissions}>
          <StatisticsBlock statisticItemList={statisticsData} />
        </CheckAccess>
        {
          isSmallScreen && (
            <CheckAccess permissions={dashboardPortfolioFilterViewPermissions}>
              <PortfolioFilter
                isInline
                portfolioGroupList={filteredPortfolioGroupList}
                onPortfolioFilterValuesChange={onPortfolioFilterValuesChange}
              />
            </CheckAccess>
          )
        }
        <Controls
          withNoEditMode
          withNoMultiLine
          withWidgetSettings={!withNoWidgets}
          widgetSettings={extendedDashboardWidgetSettings}
          createButtonTitle={createButtonTitle}
          createPermission={DEAL_CREATE}
          createGroupButtonTitle={createGroupButtonTitle}
          createGroupPermission={ACTIVITY_CREATE}
          createAdditionalItemButtonTitle={createAdditionalItemButtonTitle}
          createAdditionalItemPermission={CONTACT_CREATE}
          onCancelForm={onCancelForm}
          onCreateButtonClick={onNewDealCreateButtonClick}
          onCreateGroupButtonClick={onNewActivityCreateButtonClick}
          onCreateAdditionalItemButtonClick={onNewContactCreateButtonClick}
          onWidgetSettingsChange={onWidgetSettingsChange}
        />
        <LimitedPageContainer isActive>
          <WidgetRow>
            <CheckAccess permissions={dashboardAccountSummaryPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_ACCOUNT_SUMMARY}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetAccountSummary
                  accountSummaryData={accountSummaryData}
                  widgetSettings={dashboardWidgetSettings}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardAssetLocationsViewPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_DEAL_LOCATIONS}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetDealLocations
                  assetList={assetList}
                  portfolioList={portfolioList}
                  widgetSettings={dashboardWidgetSettings}
                  onWidgetSettingsChange={onWidgetSettingsChange}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardDealSourcesViewPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_DEAL_SOURCES_BY_BROKER}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetDealSourcesByBroker
                  contactListData={dealSourcesByBrokerData}
                  widgetSettings={dashboardWidgetSettings}
                  onWidgetSettingsChange={onWidgetSettingsChange}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardDealsInDueDiligenceViewPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_DEALS_IN_DUE_DILIGENCE}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetDealsInDueDiligence
                  dealListData={dealsInDueDiligenceData}
                  widgetSettings={dashboardWidgetSettings}
                  onWidgetSettingsChange={onWidgetSettingsChange}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardRecentDealUpdatesViewPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_RECENT_DEAL_UPDATES}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetRecentDealUpdates
                  isActivityCommentSaving={isActivityCommentSaving}
                  dealListData={recentlyUpdatedDealsData}
                  newCommentList={newCommentList}
                  currentUser={currentUser}
                  widgetSettings={dashboardWidgetSettings}
                  contactCompanyList={contactCompanyList}
                  contactPersonList={contactPersonList}
                  contactLinkedUserList={contactLinkedUserList}
                  tagList={tagList}
                  onContactCompanySearch={onContactCompanySearch}
                  onContactCompanySearchClose={onContactCompanySearchClose}
                  onContactPersonSearch={onContactPersonSearch}
                  onContactPersonSearchClose={onContactPersonSearchClose}
                  onAddNewDealActivityComment={onAddNewDealActivityComment}
                  onUpdateNewDealActivityComment={onUpdateNewDealActivityComment}
                  onDeleteNewDealActivityComment={onDeleteNewDealActivityComment}
                  onCommentEntityLinkClick={onCommentEntityLinkClick}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardUserDealTasksPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_USER_DEAL_TASKS}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetUserDealTasks
                  dealListData={userDealTasksData}
                  widgetSettings={dashboardWidgetSettings}
                  currentUser={currentUser}
                  onWidgetSettingsChange={onWidgetSettingsChange}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardUserMentionsPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_USER_MENTIONS}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetUserMentions
                  commentListData={userMentionsData}
                  widgetSettings={dashboardWidgetSettings}
                  currentUser={currentUser}
                  onWidgetSettingsChange={onWidgetSettingsChange}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardNewDealsByPeriodPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_NEW_DEALS_BY_PERIOD}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetNewDealsByPeriod
                  periodDealListData={newDealsByPeriodData}
                  widgetSettings={dashboardWidgetSettings}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardDeadDealReasonsPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_DEAD_DEAL_REASONS}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetDeadDealReasons
                  deadDealReasonListData={deadDealReasonsData}
                  widgetSettings={dashboardWidgetSettings}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardDealsByMsaPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_DEALS_BY_MSA}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetDealsByMsa
                  msaDealListData={dealsByMsaData}
                  widgetSettings={dashboardWidgetSettings}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardDealsByTypePermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_DEALS_BY_TYPE}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetDealsByType
                  dealTypeListData={dealsByTypeData}
                  widgetSettings={dashboardWidgetSettings}
                />
              </FilteredWidget>
            </CheckAccess>
            <CheckAccess permissions={dashboardDealPipelineFunnelPermissions}>
              <FilteredWidget
                widgetName={WIDGET_NAME_DEAL_PIPELINE_FUNNEL}
                widgetSettings={dashboardWidgetSettings}
                customWidgetSettings={accountWidgetSettings}
              >
                <DashboardWidgetDealPipelineFunnel
                  widgetSettings={dashboardWidgetSettings}
                  dealStatusGroupListData={dealPipelineFunnelData}
                  onWidgetSettingsChange={onWidgetSettingsChange}
                />
              </FilteredWidget>
            </CheckAccess>
          </WidgetRow>
        </LimitedPageContainer>
      </PullToRefresh>
    </CheckAccess>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    dashboard,
    portfolioFilter,
    activityList,
    activity,
  } = reducers;

  return {
    isDataLoaded: !!dashboard.dashboardData,
    isActivityCommentSaving: activity.isCommentSaving,
    portfolioIdsFilter: portfolioFilter.portfolioIds,
    portfolioGroupIdsFilter: portfolioFilter.portfolioGroupIds,
    newCommentList: activityList.commentList,
    currentUser: app.baseData && app.baseData.currentUser,
    statisticsData: dashboard.dashboardData && dashboard.dashboardData.statisticsData,
    portfolioGroupList: dashboard.dashboardData && dashboard.dashboardData.portfolioGroupList,
    assetList: dashboard.dashboardData && dashboard.dashboardData.assetList,
    contactCompanyList: dashboard.dashboardData && dashboard.dashboardData.contactCompanyList,
    contactPersonList: dashboard.dashboardData && dashboard.dashboardData.contactPersonList,
    contactLinkedUserList: dashboard.dashboardData && dashboard.dashboardData.contactLinkedUserList,
    tagList: dashboard.dashboardData && dashboard.dashboardData.tagList,
    accountSummaryData: dashboard.dashboardData && dashboard.dashboardData.accountSummaryData,
    dealsInDueDiligenceData: dashboard.dashboardData && dashboard.dashboardData.dealsInDueDiligenceData,
    dealSourcesByBrokerData: dashboard.dashboardData && dashboard.dashboardData.dealSourcesByBrokerData,
    recentlyUpdatedDealsData: dashboard.dashboardData && dashboard.dashboardData.recentlyUpdatedDealsData,
    userDealTasksData: dashboard.dashboardData && dashboard.dashboardData.userDealTasksData,
    userMentionsData: dashboard.dashboardData && dashboard.dashboardData.userMentionsData,
    newDealsByPeriodData: dashboard.dashboardData && dashboard.dashboardData.newDealsByPeriodData,
    deadDealReasonsData: dashboard.dashboardData && dashboard.dashboardData.deadDealReasonsData,
    dealsByMsaData: dashboard.dashboardData && dashboard.dashboardData.dealsByMsaData,
    dealsByTypeData: dashboard.dashboardData && dashboard.dashboardData.dealsByTypeData,
    dealPipelineFunnelData: dashboard.dashboardData && dashboard.dashboardData.dealPipelineFunnelData,
  };
};

export default connect(mapState)(Dashboard);
